import React from "react";
import { Col, Row, Container } from "react-bootstrap";
import getDisplayFields from "@app/lib/hooks/getDisplayFields";
import { getInitialValues, filterFollowUp } from "@app/components/FormBuilder/FormBuilder";
import FieldHelper from "@app/components/FormikHelper";
import FieldArrayHelper from "@app/components/FormikHelper/FieldArrayHelper";
import Accordion from "@app/components/Accordion";
import AttributeSelector from "@app/components/AttributeSelector";
import Checkbox from "@app/components/Checkbox";
import CheckboxTeaser from "@app/components/CheckboxTeaser";
import ExtendedPropertyList from "@app/components/ExtendedPropertyList";
import Hint from "@app/components/Hint";
import Panel from "@app/components/Panel";
import Radio from "@app/components/Radio";
import RadioSwitcher from "@app/components/RadioSwitcher";
import RepeatableList from "@app/components/RepeatableList";
import Select from "@app/components/Select";
import Separator from "@app/components/Separator";
import Signature from "@app/components/Signature";
import Textfield from "@app/components/Textfield";
import Datepicker from "@app/components/Datepicker";
import JobSelection from "@app/components/JobSelection";
import RadioSwitcherList from "@app/components/RadioSwitcherList";
import RadioTab from "@app/components/RadioTab";
import DangerSelect from "@app/components/Select/DangerSelect";
import BodypartSelector from "@app/components/BodypartSelector";
import InspectionResultBox from "@app/components/InspectionResultBox";

const colSizes = (cols = {}, defaults = {}) => {
	return { ...defaults, ...cols };
};

const FormBuilderElements = ({ values, displayFields, isFollowUp, instantErrorFields, instantErrors }) => {
	return displayFields.map(
		({ elementType, name, label, layout, disabled = false, tooltip, ...specialProps }, index) => {
			const required = specialProps?.validation?.required ?? false;
			const instantRequired = instantErrorFields?.includes(name) || false; // TODO
			const formGroupElements = specialProps?.formGroup?.elements
				? specialProps?.formGroup?.elements.filter((element) => filterFollowUp(isFollowUp, element))
				: undefined;

			return (
				<Col
					key={index}
					className={`col-element-${elementType}`}
					style={specialProps.flushTop ? { marginTop: 5 } : undefined}
					{...colSizes({ sm: { span: layout?.cols ?? 12, offset: layout?.offset ?? undefined } }, { xs: 12 })}
				>
					{(() => {
						switch (elementType) {
							case "headline":
								return (
									<>
										<h1 className={"mb-0"}>{specialProps.headline}</h1>
										{specialProps.subline && (
											<p className={"text-small text-dim"}>{specialProps.subline}</p>
										)}
									</>
								);

							case "subHeadline":
								return <div className={"fw-bold text-big"}>{specialProps.headline}</div>;

							case "separator":
								return <Separator />;

							case "paragraph":
								return (
									<p
										className={specialProps.classNames}
										dangerouslySetInnerHTML={{ __html: specialProps.copy }}
									></p>
								);

							case "spacer":
								return <div className={`mb-${specialProps.size}`} />;

							case "accordion":
								return (
									<Accordion key={name} headline={label} icon={specialProps?.icon}>
										<Container className={"mt-12"}>
											<Row>
												<FormBuilderElements
													values={{
														...getInitialValues(specialProps.formGroup.elements),
														...values,
													}}
													displayFields={getDisplayFields(specialProps.formGroup.elements, {
														...getInitialValues(specialProps.formGroup.elements),
														...values,
													})}
												/>
											</Row>
										</Container>
									</Accordion>
								);

							case "text":
								return (
									<FieldHelper
										key={name}
										name={name}
										type={elementType}
										component={Textfield}
										label={label}
										value={values?.[name]}
										disabled={disabled}
										required={required}
										tooltip={tooltip}
										instantRequired={instantRequired}
										//defaultValue={values?.[name]}
									/>
								);

							case "formattedNumber":
								return (
									<FieldHelper
										key={name}
										name={name}
										type={"text"}
										component={Textfield}
										label={label}
										value={values?.[name]}
										//defaultValue={values?.[name]}
										format={specialProps.format}
										disabled={disabled}
										required={required}
										tooltip={tooltip}
										instantRequired={instantRequired}
									/>
								);

							case "textArea":
								return (
									<FieldHelper
										key={name}
										name={name}
										type={elementType}
										component={Textfield}
										label={label}
										value={values?.[name]}
										//defaultValue={values?.[name]}
										charCountLabelPrefix={specialProps.charCountLabelPrefix ?? "Noch"}
										charCountLabelSufix={specialProps.charCountLabelSufix ?? "Zeichen übrig"}
										disabled={disabled}
										required={required}
										tooltip={tooltip}
										instantRequired={instantRequired}
										rows={specialProps.rows}
										placeholder={specialProps.placeholder}
									/>
								);

							case "autocomp":
								return (
									<FieldHelper
										key={name}
										name={name}
										type={elementType}
										component={Textfield}
										label={label}
										value={values?.[name]}
										//defaultValue={values?.[name]}
										hasAutocomplete={true}
										autocompleteOptions={specialProps.options}
										disabled={disabled}
										required={required}
										tooltip={tooltip}
										instantRequired={instantRequired}
									/>
								);

							case "jobSelector":
								return (
									<FieldHelper
										key={name}
										name={name}
										jobs={specialProps.jobs}
										type={elementType}
										component={JobSelection}
										label={label}
										value={values?.[name]}
										disabled={disabled}
										required={required}
										tooltip={tooltip}
										values={values}
										instantRequired={instantRequired}
									/>
								);

							case "datepicker":
								return (
									<FieldHelper
										key={name}
										name={name}
										component={Datepicker}
										label={label}
										value={values?.[name]}
										//defaultValue={values?.[name]}
										disabled={disabled}
										required={required}
										instantRequired={instantRequired}
										maxDate={specialProps.maxDate}
										minDate={specialProps.minDate}
										tight={specialProps.tight}
									/>
								);

							case "checkbox":
								return (
									<FieldHelper
										key={name}
										name={name}
										type={elementType}
										component={Checkbox}
										label={label}
										value={values?.[name]}
										disabled={disabled}
										required={required}
										isSwitch={specialProps?.isSwitch ?? false}
										defaultValue={values?.[name]}
										instantRequired={instantRequired}
									/>
								);

							case "checkboxTeaser":
								return (
									<FieldHelper
										key={name}
										name={name}
										type={elementType}
										component={CheckboxTeaser}
										label={label}
										value={values?.[name]}
										disabled={disabled}
										required={required}
										//defaultValue={values?.[name]}
										instantRequired={instantRequired}
									/>
								);

							case "radio":
								return (
									<>
										{label && (
											<p
												className={"text-small mb-12"}
												dangerouslySetInnerHTML={{ __html: label }}
											/>
										)}
										<FieldArrayHelper
											key={name}
											name={name}
											renderFlex={true}
											itemsProRow={2}
											disabled={disabled}
											required={required}
											components={specialProps.options.map((option) => (
												<FieldHelper
													component={Radio}
													key={option.value}
													name={name}
													id={`${name}_${option.value}`}
													label={option.label}
													value={option.value}
													// defaultValue={values?.[name]}
												/>
											))}
											instantRequired={instantRequired}
										/>
									</>
								);

							case "radioSwitcher":
								return (
									<div className={"auto"}>
										{label && <p className={"mb-6"}>{label}</p>}
										<FieldArrayHelper
											key={name}
											name={name}
											className={"radio-switcher-container"}
											disabled={disabled}
											required={required}
											components={specialProps.options.map((option) => (
												<FieldHelper
													component={RadioSwitcher}
													key={option.value}
													name={`${name}_${option.value}`} // TODO wieso unterschiedliche namen?
													id={`${name}_${option.value}`}
													label={option.label}
													value={option.value}
													// defaultValue={values?.[name]}
												/>
											))}
											instantRequired={instantRequired}
										/>
									</div>
								);

							case "radioTabs":
								return (
									<div className={"auto"}>
										{label && <p className={"mb-6"}>{label}</p>}
										<FieldArrayHelper
											key={name}
											name={name}
											className={"radio-tabs-container"}
											disabled={disabled}
											required={required}
											components={specialProps.options.map((option) => (
												<FieldHelper
													component={RadioTab}
													key={option.value}
													// name={`${name}_${option.value}`}
													id={`${name}_${option.value}`}
													label={option.label}
													value={option.value}
													// defaultValue={values?.[name]}
												/>
											))}
											instantRequired={instantRequired}
										/>
									</div>
								);

							case "select":
								return (
									<FieldHelper
										key={name}
										name={name}
										component={Select}
										isMulti={specialProps.multiselect}
										options={specialProps.options ?? []}
										label={label}
										disabled={disabled}
										required={required}
										placeholder={specialProps.placeholder || "Bitte wählen"}
										instantRequired={instantRequired}
										selectAllOption={specialProps.selectAllOption}
									/>
								);

							case "dangerSelect":
								return (
									<FieldHelper
										key={name}
										name={name}
										component={DangerSelect}
										isMulti={false}
										options={specialProps.options ?? []}
										label={label}
										disabled={disabled}
										required={required}
										placeholder={specialProps.placeholder || "Bitte wählen"}
										instantRequired={instantRequired}
									/>
								);

							case "followUp":
								return (
									<Panel
										hasBg={true}
										header={{
											colored: true,
											label: specialProps.formGroup.title,
											// cells: [
											// {
											// 	label: "Datum",
											// 	value: "01.01.1970",
											// },
											// ],
										}}
									>
										<Container className={"mt-12"}>
											<Row>
												<FormBuilderElements
													values={{
														...getInitialValues(formGroupElements),
														...values,
													}}
													displayFields={getDisplayFields(formGroupElements, {
														...getInitialValues(formGroupElements),
														...values,
													})}
												/>
											</Row>
										</Container>
									</Panel>
								);

							case "extendedPropertyList":
								return (
									<>
										{label && (
											<p
												className={"text-small mb-12"}
												dangerouslySetInnerHTML={{ __html: label }}
											/>
										)}
										<ExtendedPropertyList
											name={name}
											type={specialProps.extendedPropertyListType}
											overlayType={specialProps.extendedPropertyListOverlayType}
											title={specialProps.formGroup.title}
											button={
												specialProps.formGroup.button === ""
													? undefined
													: specialProps.formGroup.button
											}
											values={values}
											fieldsets={[
												{
													elements: specialProps?.formGroup?.elements,
												},
											]}
											isFollowUp={isFollowUp}
											disabled={disabled}
											required={required}
											observerProps={specialProps.observerProps}
											listCategories={specialProps.listCategories}
											validation={specialProps.validation}
											instantErrors={instantErrors}
										/>
									</>
								);

							case "subFormPage":
								//@todo: get subformdata with extendedFormKey
								return (
									<>
										{label && (
											<p
												className={"text-small mb-12"}
												dangerouslySetInnerHTML={{ __html: label }}
											/>
										)}
										<ExtendedPropertyList
											name={name}
											type={"list"}
											overlayType={"page"}
											values={values}
											title={specialProps?.formPage?.header}
											fieldsets={specialProps?.formPage?.fieldsets}
											isFollowUp={isFollowUp}
											formPage={specialProps?.formPage}
											disabled={disabled}
											required={required}
											listCategories={specialProps.listCategories}
											validation={specialProps.validation}
											extendedSummary
											button={specialProps.button}
											emptyMessage={specialProps.emptyMessage}
											instantErrors={instantErrors}
										/>
									</>
								);

							case "repeatableList":
								return (
									<>
										{label && (
											<p
												className={"text-small mb-12"}
												dangerouslySetInnerHTML={{ __html: label }}
											/>
										)}
										<RepeatableList
											name={name}
											elements={formGroupElements}
											values={getInitialValues(formGroupElements)}
											disabled={disabled}
											required={required}
										/>
									</>
								);

							case "definitionRow":
								return (
									<Row>
										<Col xs={"auto"} className={"fw-bold"}>
											{specialProps.key}
										</Col>
										<Col xs={"auto"}>{specialProps.value}</Col>
									</Row>
								);

							case "hint":
								return <Hint label={label} simple={specialProps.simple} inline={specialProps.inline} />;

							case "typicalRisksHint":
								return (
									<Panel hasBg={true} warning={false}>
										<div className={"font-bold mb-18 display-6"}>{label}</div>
										<ol>
											{specialProps.riskItems?.map((el) => (
												<li>{el}</li>
											))}
										</ol>
									</Panel>
								);

							case "attributeSelector":
								return (
									<>
										{label && (
											<p
												className={"text-small mb-12"}
												dangerouslySetInnerHTML={{ __html: label }}
											/>
										)}
										<AttributeSelector
											name={name}
											options={specialProps.options}
											useSingle={specialProps?.useSingle}
											additionalButton={specialProps?.additionalButton}
											expandable={specialProps?.expandable}
										/>
									</>
								);

							case "bodypartSelector":
								return (
									<>
										<BodypartSelector name={name} options={specialProps.options} label={label} />
									</>
								);

							case "radioSwitcherList":
								return (
									<>
										<RadioSwitcherList
											className={"mt-24"}
											id={specialProps.id}
											label={label}
											entries={specialProps.entries}
											headerLabel={specialProps.headerLabel}
											radioOptions={specialProps.radioOptions}
										/>
									</>
								);

							case "signature":
								return (
									<FieldHelper
										key={name}
										name={name}
										type={elementType}
										component={Signature}
										label={label}
										value={values?.[name]}
										disabled={disabled}
										required={required}
										instantRequired={instantRequired}
									/>
								);

							case "inspectionResultBox":
								return (
									<>
										<InspectionResultBox
											name={name}
											label={label}
											deploymentState={specialProps.deploymentState}
											steps={specialProps.steps}
										/>
									</>
								);

							default:
								return <div key={name}>Unknown form element &apos;{elementType}&apos;</div>;
						}
					})()}
				</Col>
			);
		},
	);
};

FormBuilderElements.propTypes = {};

export default FormBuilderElements;
